import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useSliderValue } from '../__hooks/useSliderValue';

import styles from './SliderValue.module.scss';
interface SliderValueProps<Value> {
    value: Value[];
    format?: (value: Value) => string;
    isReadOnly?: boolean;
    onChange?: (value: string[]) => void;
}

const SliderValue = <Value,>({
    value,
    isReadOnly = false,
    format,
    onChange,
}: SliderValueProps<Value>) => {
    const { t } = useTranslation();

    const [initialFirstValue, initialLastValue] = useMemo(() => {
        return value.map((v) => format?.(v) ?? `${v}`);
    }, [format, value]);

    const {
        firstValue,
        lastValue,
        recentValues,
        onBlurStart,
        onBlurEnd,
        onFocus,
        onKeyUp,
        onKeyDown,
        setFirstValue,
        setLastValue,
    } = useSliderValue({ initialFirstValue, initialLastValue, onChange });

    if (isReadOnly) {
        return (
            <div className={styles.LabelWrapper}>
                <span>{firstValue}</span>
                <span>{lastValue}</span>
            </div>
        );
    }

    return (
        <div className={styles.NumberInputWrapper}>
            <span>
                <input
                    type="text"
                    value={firstValue}
                    placeholder={t('TRANS-start-from')}
                    onChange={(e) => {
                        recentValues.current = [e.target.value, lastValue];
                        setFirstValue(e.target.value);
                    }}
                    onBlur={() => onBlurStart(recentValues.current[0])}
                    onFocus={() => {
                        onFocus();
                        setFirstValue('');
                    }}
                    onKeyUp={onKeyUp}
                    onKeyDown={onKeyDown}
                />
            </span>
            <span>
                <input
                    type="text"
                    value={lastValue}
                    placeholder={t('TRANS-until')}
                    onChange={(e) => {
                        recentValues.current = [firstValue, e.target.value];
                        setLastValue(e.target.value);
                    }}
                    onBlur={() => onBlurEnd(recentValues.current[1])}
                    onFocus={() => {
                        onFocus();
                        setLastValue('');
                    }}
                    onKeyUp={onKeyUp}
                    onKeyDown={onKeyDown}
                />
            </span>
        </div>
    );
};

export default SliderValue;
